import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

type Question = {
  question: string;
  image: string;
  options: string[];
  answer: string;
};

const ALL_QUESTIONS: Question[] = [
  // أجهزة الحاسوب
  { question: "ما اسم هذا الجهاز؟", image: "🖥️", options: ["حاسوب", "هاتف", "تلفاز", "راديو"], answer: "حاسوب" },
  { question: "ما هو هذا الجزء من الحاسوب؟", image: "⌨️", options: ["لوحة المفاتيح", "الشاشة", "الفأرة", "السماعة"], answer: "لوحة المفاتيح" },
  { question: "ما وظيفة هذا الجهاز؟", image: "🖨️", options: ["طباعة الأوراق", "تشغيل الموسيقى", "التصوير", "الاتصال"], answer: "طباعة الأوراق" },
  { question: "ماذا نستخدم للنقر والتحريك؟", image: "🖱️", options: ["الفأرة", "لوحة المفاتيح", "الشاشة", "السماعة"], answer: "الفأرة" },
  { question: "أين نرى ما يعرضه الحاسوب؟", image: "🖥️", options: ["الشاشة", "الطابعة", "لوحة المفاتيح", "السماعة"], answer: "الشاشة" },
  { question: "ما وظيفة السماعات؟", image: "🔊", options: ["إخراج الصوت", "إدخال النص", "طباعة الصور", "حفظ الملفات"], answer: "إخراج الصوت" },
  { question: "أين نحفظ ملفاتنا؟", image: "💾", options: ["القرص الصلب", "الشاشة", "لوحة المفاتيح", "الفأرة"], answer: "القرص الصلب" },
  { question: "ماذا يفعل زر الطاقة؟", image: "🔘", options: ["تشغيل/إيقاف الجهاز", "فتح الإنترنت", "طباعة ورقة", "حذف ملف"], answer: "تشغيل/إيقاف الجهاز" },
  { question: "ما هي الكاميرا في الحاسوب؟", image: "📷", options: ["كاميرا الويب", "الفأرة", "المايكروفون", "السماعة"], answer: "كاميرا الويب" },
  { question: "ماذا نستخدم للتحدث عبر الحاسوب؟", image: "🎤", options: ["المايكروفون", "الشاشة", "الطابعة", "القرص"], answer: "المايكروفون" },
  // الإنترنت والشبكات
  { question: "ما هو الإنترنت؟", image: "🌐", options: ["شبكة تربط الحواسيب", "نوع من الألعاب", "جهاز كهربائي", "برنامج رسم"], answer: "شبكة تربط الحواسيب" },
  { question: "ما هو المتصفح؟", image: "🔍", options: ["برنامج لتصفح الإنترنت", "جهاز للطباعة", "لعبة حاسوب", "نوع من الشاشات"], answer: "برنامج لتصفح الإنترنت" },
  { question: "ما هو البريد الإلكتروني؟", image: "📧", options: ["رسائل عبر الإنترنت", "لعبة حاسوب", "نوع من الطابعات", "برنامج رسم"], answer: "رسائل عبر الإنترنت" },
  { question: "ماذا نسمي الصور الصغيرة على سطح المكتب؟", image: "📁", options: ["أيقونات", "رسائل", "ملصقات", "أزرار"], answer: "أيقونات" },
  { question: "ما هو الفيروس في الحاسوب؟", image: "🦠", options: ["برنامج ضار", "لعبة ممتعة", "نوع شاشة", "جهاز جديد"], answer: "برنامج ضار" },
  { question: "ما هو الواي فاي (WiFi)؟", image: "📶", options: ["إنترنت لاسلكي", "نوع حاسوب", "لعبة إلكترونية", "شاشة ذكية"], answer: "إنترنت لاسلكي" },
  { question: "ما وظيفة محرك البحث؟", image: "🔎", options: ["البحث عن معلومات", "طباعة الصور", "تسجيل الصوت", "تشغيل الألعاب"], answer: "البحث عن معلومات" },
  // البرمجة
  { question: "ما هي البرمجة؟", image: "👨‍💻", options: ["كتابة أوامر للحاسوب", "رسم الصور", "تصليح الأجهزة", "كتابة القصص"], answer: "كتابة أوامر للحاسوب" },
  { question: "ما هو الكود البرمجي؟", image: "💻", options: ["تعليمات يفهمها الحاسوب", "رسالة بريدية", "صورة رقمية", "ملف صوتي"], answer: "تعليمات يفهمها الحاسوب" },
  { question: "ما هو الروبوت؟", image: "🤖", options: ["آلة تعمل بالبرمجة", "نوع حاسوب", "لعبة يدوية", "جهاز طباعة"], answer: "آلة تعمل بالبرمجة" },
  { question: "ما هو الخطأ البرمجي (Bug)؟", image: "🐛", options: ["خطأ في الكود", "فيروس خطير", "لعبة برمجة", "نوع حاسوب"], answer: "خطأ في الكود" },
  { question: "ما معنى كلمة 'خوارزمية'؟", image: "📋", options: ["خطوات لحل مشكلة", "نوع حاسوب", "لغة أجنبية", "برنامج رسم"], answer: "خطوات لحل مشكلة" },
  { question: "أي لغة برمجة يتعلمها الأطفال عادة؟", image: "🧩", options: ["سكراتش (Scratch)", "جافا", "بايثون", "سي بلس"], answer: "سكراتش (Scratch)" },
  // التطبيقات والبرامج
  { question: "ما هو التطبيق؟", image: "📱", options: ["برنامج على الجهاز", "قطعة من الحاسوب", "نوع شاشة", "كابل إنترنت"], answer: "برنامج على الجهاز" },
  { question: "ماذا نستخدم لكتابة النصوص؟", image: "📝", options: ["معالج النصوص", "الآلة الحاسبة", "برنامج الرسم", "المتصفح"], answer: "معالج النصوص" },
  { question: "ما وظيفة برنامج الرسم؟", image: "🎨", options: ["رسم وتلوين الصور", "كتابة الأكواد", "تصفح الإنترنت", "حساب الأرقام"], answer: "رسم وتلوين الصور" },
  { question: "ما هو نظام التشغيل؟", image: "⚙️", options: ["برنامج يدير الحاسوب", "قطعة معدنية", "شاشة عرض", "لوحة مفاتيح"], answer: "برنامج يدير الحاسوب" },
  { question: "ما وظيفة برنامج العروض التقديمية؟", image: "📊", options: ["عرض شرائح ومعلومات", "تشغيل ألعاب", "إرسال رسائل", "تحرير فيديو"], answer: "عرض شرائح ومعلومات" },
  { question: "ما هو متجر التطبيقات؟", image: "🏪", options: ["مكان لتحميل التطبيقات", "موقع للتسوق", "لعبة إلكترونية", "برنامج محادثة"], answer: "مكان لتحميل التطبيقات" },
  // الأمان والسلامة الرقمية
  { question: "ما هي كلمة المرور؟", image: "🔑", options: ["رمز سري لحماية الحساب", "اسم المستخدم", "عنوان البريد", "رقم الهاتف"], answer: "رمز سري لحماية الحساب" },
  { question: "لماذا لا نشارك معلوماتنا مع الغرباء؟", image: "🛡️", options: ["لحماية خصوصيتنا", "لأنها مملة", "لأنها صعبة", "لا يوجد سبب"], answer: "لحماية خصوصيتنا" },
  { question: "ما هو التخزين السحابي؟", image: "☁️", options: ["حفظ الملفات على الإنترنت", "تخزين في الثلاجة", "حفظ على ورق", "نوع قرص صلب"], answer: "حفظ الملفات على الإنترنت" },
  { question: "ما هو QR Code؟", image: "📲", options: ["رمز يُقرأ بالكاميرا", "نوع شاشة", "لعبة إلكترونية", "برنامج رسم"], answer: "رمز يُقرأ بالكاميرا" },
];

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function ComputerGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [questions] = useState(() => shuffleArray([...ALL_QUESTIONS]).slice(0, totalRounds));
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const data = questions[round] || questions[0];

  const { speak, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected !== null || round >= totalRounds) return;
      const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF\s]/g, "").trim();
      const spoken = normalizeAr(transcript);
      const match = data.options.find(opt => {
        const n = normalizeAr(opt);
        return spoken.includes(n) || n.includes(spoken);
      });
      if (match) handleSelect(match);
    },
  });

  const handleSelect = (opt: string) => {
    if (selected !== null) return;
    setSelected(opt);
    const correct = opt === data.answer;
    if (correct) {
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setRound((r) => r + 1);
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setSelected(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="text-6xl">
              💻
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">أحسنت! 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-600" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-700 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak(data.question + ". الخيارات: " + data.options.join("، "))}>
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`} onClick={() => isListening ? stopListening() : startListening()} disabled={!!selected}>
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <motion.span className="text-7xl block mb-4" animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>{data.image}</motion.span>
                <p className="text-lg font-medium opacity-95">{data.question}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-4 text-center transition-all ${bgClass}`}
            >
              <span className="text-base font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
